import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { useGetUnreadQuery } from 'src/api/getstream-api';
import { useCapabilities } from 'src/hooks/use-capabilities';
import { hasFeatureFlag } from 'src/libs/feature-flags';
import { paths } from 'src/paths';
import { icons } from 'src/theme/icons';

export const MOBILE_BREAKPOINT = 'lg';
export const SIDE_NAV_WIDTH: number = 280;
export const TOP_NAV_HEIGHT: number = 64;

export type NavigationColor = 'blend-in' | 'discreet' | 'evident';

export type NavigationItemActiveMatcher =
  | ((pathname: string) => boolean)
  | RegExp
  | string;

export type NavigationItemActive =
  | NavigationItemActiveMatcher
  | NavigationItemActiveMatcher[];

export interface NavigationItem {
  icon?: string;
  title: string;
  path?: string;
  items?: NavigationItem[];
  disabled?: boolean;
  external?: boolean;
  hidden?: boolean;
  badge?: boolean;
  active?: NavigationItemActive;
}

export interface Section {
  items: NavigationItem[];
  subheader?: string;
}

export const useSections = () => {
  const can = useCapabilities();
  const chatNotifications = useGetUnreadQuery();

  return useMemo(() => {
    const userSections: Section[] = [
      {
        items: [
          {
            title: 'Aktuelles',
            path: paths.index,
            icon: 'fa6-solid:newspaper',
            active: [/^\/?$/, /^\/post(\/.*)?$/]
          },
          {
            title: 'Kalender',
            path: paths.calendar,
            icon: 'fa6-solid:calendar-days',
            active: /^\/calendar(\/.*)?$/
          },
          {
            title: 'Dokumente',
            path: paths.documents,
            icon: 'fa6-solid:folder',
            active: /^\/documents(\/.*)?$/
          },
          {
            title: 'Chat',
            path: paths.chat,
            icon: 'fa6-solid:comment-dots',
            hidden: !hasFeatureFlag('chat'),
            badge: !!chatNotifications.data?.total_unread_count,
            active: /^\/chat(\/.*)?$/
          },
          {
            title: 'Wiki',
            path: paths.wiki,
            icon: 'fa6-solid:book',
            hidden: !hasFeatureFlag('wiki'),
            active: /^\/wiki(\/.*)?$/
          }
        ].filter((section) => section !== undefined)
      }
    ];
    const contentSections: Section[] = [
      {
        subheader: 'Inhalte erstellen',
        items: [
          ['content', 'Beiträge', 'fa6-solid:file-lines'] as const,
          [
            'newsletter',
            'Newsletter',
            'fa6-solid:envelope-open-text',
            !hasFeatureFlag('newsletter')
          ] as const,
          [
            'occasion',
            'Veranstaltungen',
            'fa6-solid:calendar',
            !hasFeatureFlag('occasions')
          ] as const
        ].map(([id, title, icon, hidden]) => ({
          title,
          icon,
          active: new RegExp(`^/${id}(\\/.*)?$`),
          hidden: !can['editor.*'] || hidden,
          items: [
            {
              title: 'Erstellen',
              path: paths[id].create,
              active: new RegExp(`^${paths[id].create}(\\/.*)?$`),
              hidden: !can['editor.draft.create']
            },
            {
              title: 'Entwürfe',
              path: paths[id].drafts,
              active: new RegExp(`^${paths[id].drafts}(\\/.*)?$`),
              hidden: !can['editor.draft.*']
            },
            {
              title: 'Geplant',
              path: paths[id].planned,
              active: new RegExp(`^${paths[id].planned}(\\/.*)?$`),
              hidden: !can['editor.planned.*']
            },
            {
              title: 'Veröffentlicht',
              path: paths[id].published,
              active: new RegExp(`^${paths[id].published}(\\/.*)?$`),
              hidden: !can['editor.published.*']
            }
          ].filter((item) => !item.hidden)
        }))
      }
    ];
    const adminSections: Section[] = [
      {
        subheader: 'Administration',
        items: [
          {
            title: 'Verwaltung',
            path: paths.admin.manage.index,
            icon: 'fa6-solid:users',
            hidden: !can['management.*'],
            active: /^\/admin\/manage(\/.*)?$/
          },
          {
            title: 'Einstellungen',
            path: paths.admin.settings.index,
            icon: 'fa6-solid:gear',
            hidden: !can['settings.*'],
            active: /^\/admin\/settings(\/.*)?$/
          }
        ].filter((section) => section !== undefined)
      }
    ];
    const accountSections: Section[] = [
      {
        subheader: 'Einstellungen',
        items: [
          {
            title: 'Mein Betrieb',
            path: paths.company.user,
            icon: 'fa6-solid:building',
            active: /^\/company(\/.*)?$/
          },
          {
            title: 'Mein Profil',
            path: paths.account,
            icon: icons.profile,
            active: /^\/account(\/.*)?$/
          }
        ]
      }
    ];

    return (
      [
        ...userSections,
        ...contentSections,
        ...adminSections,
        ...accountSections
      ]
        // remove hidden items
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => !item.hidden)
        }))
        // remove empty sections
        .filter((section) => section.items.length > 0)
    );
  }, [can, chatNotifications.data?.total_unread_count]);
};

export const useCssVars = (color: NavigationColor): Record<string, string> => {
  const theme = useTheme();

  return useMemo((): Record<string, string> => {
    switch (color) {
      case 'blend-in':
        if (theme.palette.mode === 'dark') {
          return {
            '--nav-bg': theme.palette.background.default,
            '--nav-color': theme.palette.neutral[100],
            '--nav-border-color': theme.palette.neutral[700],
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[600],
            '--nav-item-icon-color': theme.palette.neutral[500],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[700],
            '--nav-item-chevron-color': theme.palette.neutral[700],
            '--nav-scrollbar-color': theme.palette.neutral[400]
          };
        } else {
          return {
            '--nav-bg': theme.palette.background.default,
            '--nav-color': theme.palette.text.primary,
            '--nav-border-color': theme.palette.neutral[100],
            '--nav-logo-border': theme.palette.neutral[100],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.text.secondary,
            '--nav-item-hover-bg': theme.palette.action.hover,
            '--nav-item-active-bg': theme.palette.action.selected,
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[400],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[400],
            '--nav-item-chevron-color': theme.palette.neutral[400],
            '--nav-scrollbar-color': theme.palette.neutral[900]
          };
        }

      case 'discreet':
        if (theme.palette.mode === 'dark') {
          return {
            '--nav-bg': theme.palette.neutral[900],
            '--nav-color': theme.palette.neutral[100],
            '--nav-border-color': theme.palette.neutral[700],
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[600],
            '--nav-item-icon-color': theme.palette.neutral[500],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[700],
            '--nav-item-chevron-color': theme.palette.neutral[700],
            '--nav-scrollbar-color': theme.palette.neutral[400]
          };
        } else {
          return {
            '--nav-bg': theme.palette.neutral[50],
            '--nav-color': theme.palette.text.primary,
            '--nav-border-color': theme.palette.divider,
            '--nav-logo-border': theme.palette.neutral[200],
            '--nav-section-title-color': theme.palette.neutral[500],
            '--nav-item-color': theme.palette.neutral[500],
            '--nav-item-hover-bg': theme.palette.action.hover,
            '--nav-item-active-bg': theme.palette.action.selected,
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[400],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[400],
            '--nav-item-chevron-color': theme.palette.neutral[400],
            '--nav-scrollbar-color': theme.palette.neutral[900]
          };
        }

      case 'evident':
        if (theme.palette.mode === 'dark') {
          return {
            '--nav-bg': theme.palette.neutral[800],
            '--nav-color': theme.palette.common.white,
            '--nav-border-color': 'transparent',
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.common.white,
            '--nav-item-disabled-color': theme.palette.neutral[500],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[500],
            '--nav-item-chevron-color': theme.palette.neutral[600],
            '--nav-scrollbar-color': theme.palette.neutral[400]
          };
        } else {
          return {
            '--nav-bg': theme.palette.neutral[800],
            '--nav-color': theme.palette.common.white,
            '--nav-border-color': 'transparent',
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.common.white,
            '--nav-item-disabled-color': theme.palette.neutral[500],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[500],
            '--nav-item-chevron-color': theme.palette.neutral[600],
            '--nav-scrollbar-color': theme.palette.neutral[400]
          };
        }

      default:
        return {};
    }
  }, [theme, color]);
};
